import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { CaretDown, CaretUp } from 'src/common/icons';
import type { CategoryEntry, CategoryPrompt } from '../types';
import { ICON_MAP } from '../icons';
import messages from './messages';
import styles from './ChatEmptyState.module.scss';

/** Case-insensitive match on every word of the query, against the prompt's
 *  description, its name and its category title. */
export function filterCategories(categories: CategoryEntry[], query: string): CategoryEntry[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return categories;
  return categories
    .map((category) => ({
      ...category,
      prompts: category.prompts.filter((prompt) => {
        const haystack = `${prompt.description} ${prompt.name.replace(/_/g, ' ')} ${category.title}`.toLowerCase();
        return words.every((w) => haystack.includes(w));
      }),
    }))
    .filter((category) => category.prompts.length > 0);
}

interface BrowseAllPanelProps {
  categories: CategoryEntry[];
  onBack: () => void;
  onPick: (prompt: CategoryPrompt) => void;
}

/**
 * The full prompt catalogue, one click from the empty state. Rendered inside
 * the chat panel rather than as a modal: a modal would sit over the page and
 * fight the asset overlay for z-index (ui#958), and the composer the picked
 * item lands in is right below.
 *
 * Categories start collapsed; typing a search opens every category with a
 * match and shows only the matching items.
 */
export const BrowseAllPanel: React.FC<BrowseAllPanelProps> = ({ categories, onBack, onPick }) => {
  const { formatMessage } = useIntl();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const searching = query.trim().length > 0;
  const visible = useMemo(() => filterCategories(categories, query), [categories, query]);

  return (
    <div
      className={styles.browse}
      data-test="chat-browse-all"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onBack();
        }
      }}
    >
      <div className={styles.browseHeader}>
        <button
          type="button"
          className={styles.backButton}
          onClick={onBack}
          aria-label={formatMessage(messages.back)}
          data-test="chat-browse-back"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h3 className={styles.browseTitle}>{formatMessage(messages.browseTitle)}</h3>
      </div>

      <input
        ref={searchRef}
        type="search"
        className={styles.search}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={formatMessage(messages.searchPlaceholder)}
        aria-label={formatMessage(messages.searchLabel)}
        data-test="chat-browse-search"
      />

      {visible.length === 0 ? (
        <p className={styles.noMatches}>{formatMessage(messages.noMatches, { query: query.trim() })}</p>
      ) : (
        <ul className={styles.categoryList}>
          {visible.map((category) => {
            const expanded = searching || Boolean(open[category.id]);
            const listId = `chat-browse-${category.id}`;
            return (
              <li key={category.id}>
                <button
                  type="button"
                  className={styles.categoryButton}
                  aria-expanded={expanded}
                  aria-controls={listId}
                  onClick={() => setOpen((cur) => ({ ...cur, [category.id]: !cur[category.id] }))}
                  disabled={searching}
                  data-test={`chat-browse-category-${category.id}`}
                >
                  {ICON_MAP[category.icon] && (
                    <span className={styles.categoryIcon} aria-hidden>
                      {ICON_MAP[category.icon]}
                    </span>
                  )}
                  <span className={styles.categoryTitle}>{category.title}</span>
                  <span className={styles.categoryCount}>{category.prompts.length}</span>
                  {!searching && (
                    <span className={styles.categoryCaret} aria-hidden>
                      {expanded ? <CaretUp /> : <CaretDown />}
                    </span>
                  )}
                </button>
                {expanded && (
                  <ul className={styles.promptList} id={listId}>
                    {category.prompts.map((prompt) => (
                      <li key={prompt.name}>
                        <button
                          type="button"
                          className={styles.promptItem}
                          onClick={() => onPick(prompt)}
                          data-test={`chat-browse-prompt-${prompt.name}`}
                        >
                          {prompt.description}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default BrowseAllPanel;
